import { useEffect, useState } from "react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import type { LinksFunction } from "@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
  useSearchParams } from
"@remix-run/react";
import { ClientOnly } from "remix-utils/client-only";
import { cssBundleHref } from "@remix-run/css-bundle";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next";

import stylesHref from "@egjs/react-flicking/dist/flicking.css";
import flickingPlugins from "@egjs/flicking-plugins/dist/flicking-plugins.css";
import {
  AddonTypeId,
  Theme,
  getEnvVar,
  resizeImage,
  useStoreImages } from
"@commons-monorepo/index";
import { themeV2 } from "~/styles/theme";
import { GlobalStylesWrapper } from "~/styles/context/global-styles-wrapper";
import global from "@commons-monorepo/public/styles.css";
import EmptyState404 from "./components/empty-states/404/empty-404";
import {
  loadFbPixelScript,
  loadFbPixelNolScript } from
"~/common/utils/load-script";
import { FontEnum } from "~/styles/theme.type";
import { getStoreLocale, serializeTemplate } from "~/styles/util";
import { SkinList } from "~/styles/constants/skin-list";
import dayjs from "dayjs";
import { install } from "ga-gtag";
import TagManager, { TagManagerArgs } from "react-gtm-module";
import Blur from "~/components/blur/blur";
import ClosedPopup from "~/components/closed-popup/closed-popup";
import { getAddonFromStoreAddons } from "@commons-monorepo/index";
import "slick-carousel/slick/slick.css?__remix_sideEffect__";
import "slick-carousel/slick/slick-theme.css?__remix_sideEffect__";
import { fetchDisabledStores } from "./config/fetcher";
import { SumerKitAppProvider } from "@sumerkit/themes/ssr";
import { RootLoaderData } from "./common/entities/loaders";

export const links: LinksFunction = () => {
  return [
  ...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : []),
  {
    rel: "stylesheet",
    href: global
  },
  {
    rel: "stylesheet",
    href: stylesHref
  },
  {
    rel: "stylesheet",
    href: flickingPlugins
  }];

};

export { loader } from "./root.server";

export const handle = {
  i18n: ["index"]
};

const App = () => {
  const { storeMetadata, isMobile, environment, userAddons, locale, ...rest } = (
  (useLoaderData<RootLoaderData>() as unknown) as RootLoaderData);
  useChangeLanguage(getStoreLocale(storeMetadata.locale));
  const [offStore, setOffStore] = useState(false);
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const { partnerProfilePicture } = useStoreImages(storeMetadata);
  const [searchParams] = useSearchParams();

  const initTagManager = () => {
    const tagManagerArgs: TagManagerArgs = {
      gtmId: getEnvVar("PUBLIC_GOOGLE_TAG_MANAGER") || ""
    };
    if (tagManagerArgs?.gtmId) {
      TagManager.initialize(tagManagerArgs);
    }
  };

  const initGoogleTrackingIfStoreNeeds = () => {
    const storeIsNotPrime = storeMetadata?.prime?.id === undefined;
    const storeWithSevenDaysTrial = dayjs(storeMetadata.createdAt).add(
      7,
      "day"
    );
    const isStoreCreatedBeforeSevenDays = storeWithSevenDaysTrial.isBefore(
      new Date(),
      "day"
    );
    if (storeIsNotPrime && isStoreCreatedBeforeSevenDays) {
      install(getEnvVar("PUBLIC_GOOGLE_TRACKING_ID_V4"));
    }
  };

  const FB_PIXEL_STORAGE_KEY = "sumer_fb_pixel_id";
  const FB_PIXEL_STORAGE_KEY_PERSISTENT = "sumer_fb_pixel_id_persistent";

  const persistFacebookPixelId = (pixelId: string) => {
    if (typeof window === "undefined" || !pixelId) return;
    try {
      // ✅ CORRECCIÓN RENOVACIÓN: Guardar en sessionStorage y localStorage
      window.sessionStorage.setItem(FB_PIXEL_STORAGE_KEY, pixelId);
      window.localStorage.setItem(FB_PIXEL_STORAGE_KEY_PERSISTENT, pixelId);
      console.log('✅ CATALOGUE: Pixel ID guardado en sessionStorage y localStorage');
    } catch (error) {
      console.error("Failed to persist facebook pixel id", error);
    }
  };

  const installStoreTagManager = () => {
    console.log('=== 🔵 CATALOGUE: Inicializando Google Tag Manager ===');
    console.log('storeMetadata.addonUser:', !!storeMetadata.addonUser);
    console.log('googleTagManager desde addonUser:', storeMetadata.addonUser?.body?.googleTagManager);

    // ✅ CORRECCIÓN RENOVACIÓN: Fallback para Google Tag Manager
    let gtmId: string | null = null;

    // Prioridad 1: Addon activo desde storeMetadata.addonUser
    if (storeMetadata.addonUser?.body?.googleTagManager) {
      gtmId = storeMetadata.addonUser.body.googleTagManager;
      console.log('✅ CATALOGUE: Google Tag Manager ID desde addonUser:', gtmId);

      // Guardar en localStorage para persistencia
      try {
        window.localStorage?.setItem('sumer_gtm_id_persistent', gtmId);
        window.sessionStorage?.setItem('sumer_gtm_id', gtmId);
        console.log('✅ CATALOGUE: Google Tag Manager ID guardado en localStorage');
      } catch (storageError) {
        console.warn('⚠️ CATALOGUE: Error guardando GTM ID:', storageError);
      }
    } else {
      console.warn('⚠️ CATALOGUE: No hay Google Tag Manager ID en addonUser (posible renovación)');
    }

    // Prioridad 2: Fallback a sessionStorage
    if (!gtmId) {
      try {
        const storedGtmId = window.sessionStorage?.getItem('sumer_gtm_id');
        if (storedGtmId) {
          gtmId = storedGtmId;
          console.log('✅ CATALOGUE: Usando Google Tag Manager ID de sessionStorage como fallback:', gtmId);
        }
      } catch (storageError) {
        console.warn('⚠️ CATALOGUE: Error accediendo sessionStorage:', storageError);
      }
    }

    // Prioridad 3: Fallback a localStorage (persistente)
    if (!gtmId) {
      try {
        const persistentGtmId = window.localStorage?.getItem('sumer_gtm_id_persistent');
        if (persistentGtmId) {
          gtmId = persistentGtmId;
          console.log('✅ CATALOGUE: Usando Google Tag Manager ID persistente de localStorage como fallback:', gtmId);
          console.log('⚠️ NOTA: Esto puede indicar que el plan está en renovación');
        }
      } catch (storageError) {
        console.warn('⚠️ CATALOGUE: Error accediendo localStorage:', storageError);
      }
    }

    // Instalar Google Tag Manager si tenemos un ID
    if (gtmId) {
      console.log('=== ✅ CATALOGUE: Instalando Google Tag Manager ===');
      console.log('GTM ID:', gtmId);
      console.log('Fuente:', storeMetadata.addonUser?.body?.googleTagManager ? 'Addon activo' : 'Fallback (posible renovación)');
      install(gtmId);
      console.log('✅ CATALOGUE: Google Tag Manager instalado correctamente');
    } else {
      console.warn('❌ CATALOGUE: No se pudo obtener Google Tag Manager ID (ni del addon ni de fallbacks)');
    }
  };

  const initFacebookPixelIfStoreNeeds = async () => {
    const fbPixelAddon = getAddonFromStoreAddons<{fx_pixel: string;}>(
      userAddons,
      AddonTypeId.FbPixel
    );

    console.log('=== 🔵 CATALOGUE: Inicializando Facebook Pixel ===');
    console.log('Addon FbPixel encontrado:', !!fbPixelAddon);
    console.log('Addon is_active:', fbPixelAddon?.is_active);
    console.log('Total userAddons:', userAddons?.length || 0);

    // ✅ CORRECCIÓN RENOVACIÓN: Verificar is_active y usar fallback
    let pixelId: string | null = null;

    // Prioridad 1: Addon activo con fx_pixel
    if (fbPixelAddon?.body?.fx_pixel) {
      console.log('=== 🔵 CATALOGUE: Extrayendo Pixel ID desde addon activo ===');
      const match = fbPixelAddon.body.fx_pixel.match(
        /fbq[(]['"”]init['"”], ['"”](.*)['"”][)]/i
      );
      pixelId = match && match[1] ? match[1] : null;
      console.log('Pixel ID extraído:', pixelId);

      if (pixelId) {
        persistFacebookPixelId(pixelId);
      }
    } else if (fbPixelAddon && !fbPixelAddon.is_active) {
      console.warn('⚠️ CATALOGUE: Addon FbPixel encontrado pero NO está activo (posible renovación)');
      console.warn('is_active:', fbPixelAddon.is_active);
      console.warn('ends_at:', fbPixelAddon.ends_at);
      console.warn('inits_at:', fbPixelAddon.inits_at);
    } else if (userAddons && userAddons.length === 0) {
      console.warn('⚠️ CATALOGUE: No se encontraron addons (array vacío - posible renovación)');
    } else {
      console.log('⚠️ CATALOGUE: No hay addon FbPixel o fx_pixel está vacío');
    }

    // ✅ CORRECCIÓN RENOVACIÓN: Fallback si no se obtuvo Pixel ID del addon activo
    if (!pixelId) {
      console.log('=== 🔵 CATALOGUE: Intentando fallback a Pixel ID guardado ===');

      // Prioridad 2: Fallback a sessionStorage
      try {
        const storedPixelId = window.sessionStorage?.getItem(FB_PIXEL_STORAGE_KEY);
        if (storedPixelId) {
          pixelId = storedPixelId;
          console.log('✅ CATALOGUE: Usando Pixel ID de sessionStorage como fallback:', pixelId);
        }
      } catch (storageError) {
        console.warn('⚠️ CATALOGUE: Error accediendo sessionStorage:', storageError);
      }

      // Prioridad 3: Fallback a localStorage (persistente)
      if (!pixelId) {
        try {
          const persistentPixelId = window.localStorage?.getItem(FB_PIXEL_STORAGE_KEY_PERSISTENT);
          if (persistentPixelId) {
            pixelId = persistentPixelId;
            console.log('✅ CATALOGUE: Usando Pixel ID persistente de localStorage como fallback:', pixelId);
            console.log('⚠️ NOTA: Esto puede indicar que el plan está en renovación');
          }
        } catch (storageError) {
          console.warn('⚠️ CATALOGUE: Error accediendo localStorage:', storageError);
        }
      }
    }

    // Inicializar pixel si tenemos un ID (activo o fallback)
    if (pixelId) {
      console.log('=== ✅ CATALOGUE: Inicializando Pixel ===');
      console.log('Pixel ID:', pixelId);
      console.log('Fuente:', fbPixelAddon?.is_active ? 'Addon activo' : 'Fallback (posible renovación)');
      loadFbPixelNolScript(pixelId, storeMetadata.enterpriseName);
      loadFbPixelScript(pixelId);
      console.log('✅ CATALOGUE: Pixel inicializado correctamente');
    } else {
      console.warn('❌ CATALOGUE: No se pudo obtener Pixel ID (ni del addon ni de fallbacks)');
    }
  };

  const selectedTheme = (storeMetadata?.color as Theme);
  const cover = storeMetadata?.image?.cover;
  const template =
  storeMetadata?.template &&
  serializeTemplate(storeMetadata.template.template);
  const catalogueConfig = storeMetadata?.catalogueConfiguration;

  const promotionsCover = storeMetadata?.image?.promotionsCover;
  const wholesaleCover = storeMetadata?.image?.wholesaleCover;

  const templateId = storeMetadata?.template?.id || SkinList.DEFAULT;

  const theme = {
    themeColors: {
      id: selectedTheme?.id,
      name: selectedTheme?.name,
      isPrime: selectedTheme?.isPrime,
      button: selectedTheme?.palette[1].color,
      text: selectedTheme?.palette[0].color,
      background: selectedTheme?.background.color
    },
    cover,
    promotionsCover,
    wholesaleCover,
    config: catalogueConfig,
    template,
    templateId,
    fontFamily: (storeMetadata.typography as FontEnum) || FontEnum.inter,
    logo: partnerProfilePicture
  };

  const showBlur =
  storeMetadata?.catalogueBlur !== null &&
  !pathname.includes("contact-addon");

  useEffect(() => {
    initTagManager();
    initFacebookPixelIfStoreNeeds();
    initGoogleTrackingIfStoreNeeds();
    installStoreTagManager();
  }, []);

  useEffect(() => {
    if (storeMetadata) {
      fetchDisabledStores().then((storesOff) => {
        if (storesOff.indexOf(storeMetadata.ownerId) >= 0) {
          setOffStore(true);
        }
      });
    }
  }, []);
  return (
    <html
      lang={i18n.language}
      style={{ pointerEvents: searchParams.get("noEvents") === "true" ? "none" : "auto" }}
      dir={i18n.dir()}>

      <head>
        <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1'></meta>
        {isMobile && theme?.cover?.coverImage ?
        <link rel='preload' as='image' href={theme?.cover?.coverImage} /> :

        theme?.cover?.webCover && <link rel='preload' as='image' href={theme?.cover?.webCover} />}

        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' />
        <link
          href='https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap'
          rel='stylesheet' />

        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${environment.PUBLIC_GOOGLE_ANALYTICS_ID}`}>
        </script>
        <Meta />
        <Links />
        {partnerProfilePicture &&
        <link rel='icon' type='image/png' sizes='32x32' href={resizeImage(partnerProfilePicture, 32, 32, 50)} />}

      </head>
      <body
        style={
        showBlur ?
        {
          overflow: "hidden",
          height: "100vh",
          width: "100vw",
          position: "fixed"
        } :
        {}}>


        {offStore && <ClosedPopup storeMetadata={storeMetadata} />}
        {showBlur && <Blur storeMetadata={storeMetadata} />}
        <SumerKitAppProvider fontDefault={false}>
          <Outlet
            context={{
              theme,
              storeMetadata,
              isMobile,
              environment,
              userAddons,
              offStore,
              locale,
              ...rest
            }} />

        </SumerKitAppProvider>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(environment)}`
          }} />

        <ScrollRestoration />
        <Scripts />
        <LiveReload />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
            
              gtag('config', '${environment.PUBLIC_GOOGLE_ANALYTICS_ID}');
            `
          }} />

        
      </body>
    </html>);

};

export default withSentry(App);

export function CatchBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <html lang='es'>
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <GlobalStylesWrapper setFont={() => {}} theme={themeV2}>
          <EmptyState404 />
        </GlobalStylesWrapper>
        <LiveReload />
      </body>
    </html>);

}

export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <html lang='es'>
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <GlobalStylesWrapper setFont={() => {}} theme={themeV2}>
          <EmptyState404 />
        </GlobalStylesWrapper>
        <LiveReload />
      </body>
    </html>);

}

export const shouldRevalidate = () => false;